import React, { useState } from "react";
import { Link } from "react-router-dom";
import RegisterStudentForm from "./RegisterStudentForm";
import RegisterStaffForm from "./RegisterStaffForm";
import RegisterAlumniForm from "./RegisterAlumniForm";
import RegisterFacultyForm from "./RegisterFacultyForm";
import { GraduationCap, Briefcase, ArrowRight, Home, Users, UserCheck } from "lucide-react";
import { motion } from "framer-motion";
import ThemeToggle from "../common/ThemeToggle";

const Register = () => {
  const [selectedRole, setSelectedRole] = useState("");

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
  };

  if (selectedRole) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <div className="container mx-auto px-4 py-8">
          {selectedRole === "student" && <RegisterStudentForm />}
          {selectedRole === "alumni" && <RegisterAlumniForm />}
          {selectedRole === "staff" && <RegisterStaffForm />}
          {selectedRole === "faculty" && <RegisterFacultyForm />}

          <button
            className="mt-6 mx-auto block bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 font-semibold px-8 py-3 rounded-xl 
  border-2 border-gray-200 dark:border-gray-700 hover:border-usiu-blue-500 hover:shadow-lg transition-all"
            onClick={() => setSelectedRole("")}
          >
            ← Back to Role Selection
          </button>
        </div>
      </div>
    );
  }

  const roleCards = [
    {
      id: "student",
      title: "Student",
      description: "Current students",
      icon: GraduationCap,
      color: "from-usiu-blue-600 to-usiu-blue-500",
      bg: "bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20",
      border: "border-usiu-blue-200 dark:border-usiu-blue-800",
      hover: "hover:border-blue-400 dark:hover:border-blue-500"
    },
    {
      id: "alumni",
      title: "Alumni",
      description: "Graduated students",
      icon: UserCheck,
      color: "from-cyan-600 to-teal-500",
      bg: "bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20",
      border: "border-cyan-200 dark:border-cyan-800",
      hover: "hover:border-cyan-400 dark:hover:border-cyan-500"
    },
    {
      id: "staff",
      title: "Staff",
      description: "University Staff",
      icon: Briefcase,
      color: "from-purple-600 to-pink-600",
      bg: "bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20",
      border: "border-purple-200 dark:border-purple-800",
      hover: "hover:border-purple-400 dark:hover:border-purple-500"
    },
    {
      id: "faculty",
      title: "Faculty",
      description: "Teaching staff",
      icon: Users,
      color: "from-orange-500 to-amber-500",
      bg: "bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20",
      border: "border-orange-200 dark:border-orange-800",
      hover: "hover:border-orange-400 dark:hover:border-orange-500"
    }
  ];

  return (
    <div className="min-h-screen flex relative">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/USIU-Library.jpg"
          alt="USIU-Africa Library"
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-usiu-blue-50/95 dark:from-slate-900/95 dark:via-slate-800/90 dark:to-slate-900/95"></div>
      </div>

      {/* Theme Toggle - Floating Top Right */}
      <div className="absolute top-6 right-6 z-20">
        <ThemeToggle />
      </div>

      {/* Role Selection Container */}
      <div className="flex-1 flex items-center justify-center p-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-4xl"
        >
          {/* Logo */}
          <div className="mb-6 text-center">
            <Link to="/" className="inline-flex items-center gap-3">
              <img
                src="/usiu-logo-02.png"
                alt="USIU Logo"
                className="h-10 w-10 object-contain"
                onError={(e) => {
                  e.target.src = "/usiu-logo.png";
                }}
              />
              <div>
                <h1
                  className="text-xl font-bold bg-gradient-to-r from-usiu-blue-600 to-usiu-blue-500 
  dark:from-usiu-gold-400 dark:to-usiu-gold-500 bg-clip-text text-transparent"
                >
                  USIU-Africa
                </h1>
                <p className="text-xs text-center text-gray-600 dark:text-gray-300">Counseling Center</p>
              </div>
            </Link>
          </div>

          {/* Header */}
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-800 dark:from-usiu-gold-400 dark:to-usiu-gold-500 bg-clip-text text-transparent mb-2">
              Select Your Role
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Choose the option that best describes you to continue
            </p>
          </div>

          {/* Role Selection Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {roleCards.map((card) => (
              <motion.button
                key={card.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleRoleSelect(card.id)}
                className={`w-full flex items-center p-4 rounded-xl border-2 transition-all group ${card.bg} ${card.border} ${card.hover} shadow-sm hover:shadow-md`}
              >
                <div className={`bg-gradient-to-br ${card.color} p-3 rounded-lg mr-4 shrink-0`}>
                  <card.icon className="w-5 h-5 text-white" />
                </div>
                <div className="text-left flex-1 min-w-0">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">
                    {card.title}
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-300 truncate">
                    {card.description}
                  </p>
                </div>
                <ArrowRight
                  className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 
  group-hover:translate-x-1 transition-all ml-2"
                />
              </motion.button>
            ))}
          </div>

          {/* Sign In Link */}
          <p className="mt-8 text-center text-sm text-gray-600 dark:text-gray-300">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-semibold text-usiu-blue-500 hover:text-usiu-blue-600 dark:text-usiu-gold-400 dark:hover:text-usiu-gold-500 transition"
            >
              Sign in here
            </Link>
          </p>

          {/* Back to Home Button */}
          <div className="mt-4 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-usiu-blue-600 dark:hover:text-usiu-gold-400 transition"
            >
              <Home className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;
